import { Principal } from "@icp-sdk/core/principal";

export const principalRegexFlexible =
	/^[a-z0-9]{3,6}(-[a-z0-9]{3,6}){10,}$/i;
export const btcAddressRegex =
	/^(bc1p[a-z0-9]{38,60}|bc1q[a-z0-9]{38,60}|bc1[a-z0-9]{39,87}|[13][a-km-zA-HJ-NP-Z1-9]{25,34})$/i;

interface SearchResultEntity {
	principal?: string;
	btc_deposit_address?: string | null;
	btc_wallet_address?: string | null;
	rune_deposit_address?: string | null;
}

interface SearchResultItem {
	id?: string;
	type?: string;
	entity?: SearchResultEntity | null;
}

interface SearchResponse {
	data?: SearchResultItem[];
}

export type RecipientResolution =
	| {
			kind: "empty";
			input: string;
	  }
	| {
			kind: "invalid";
			input: string;
	  }
	| {
			kind: "principal_odin_user";
			input: string;
			principal: string;
	  }
	| {
			kind: "principal_non_odin_user";
			input: string;
			principal: string;
	  }
	| {
			kind: "btc_bound";
			input: string;
			address: string;
			principal: string;
	  }
	| {
			kind: "btc_external";
			input: string;
			address: string;
	  }
	| {
			kind: "lookup_error";
			input: string;
			error: string;
	  };

export type ScanResolution =
	| {
			kind: "supported_url";
			raw: string;
			url: string;
			host: string;
			internalPath: string;
	  }
	| {
			kind: "principal_odin_user";
			raw: string;
			principal: string;
	  }
	| {
			kind: "btc_bound";
			raw: string;
			address: string;
			principal: string;
	  }
	| {
			kind: "btc_external";
			raw: string;
			address: string;
	  }
	| {
			kind: "unrecognized";
			raw: string;
			displayText: string;
	  }
	| {
			kind: "lookup_error";
			raw: string;
			displayText: string;
			error: string;
	  };

function hasExactAddressMatch(
	entity: SearchResultEntity | null | undefined,
	query: string,
) {
	const normalizedQuery = query.trim().toLowerCase();
	if (!entity) return false;

	return (
		(entity.btc_wallet_address || "").toLowerCase() === normalizedQuery ||
		(entity.btc_deposit_address || "").toLowerCase() === normalizedQuery ||
		(entity.rune_deposit_address || "").toLowerCase() === normalizedQuery
	);
}

export function isValidPrincipal(value: string) {
	const trimmed = value.trim();
	if (!principalRegexFlexible.test(trimmed)) {
		return false;
	}

	try {
		Principal.fromText(trimmed);
		return true;
	} catch {
		return false;
	}
}

export function isBtcAddress(value: string) {
	return btcAddressRegex.test(value.trim());
}

export function extractBtcAddressFromQr(input: string) {
	const trimmed = input.trim();
	if (!trimmed) return "";

	if (isBtcAddress(trimmed)) {
		return trimmed;
	}

	if (!trimmed.toLowerCase().startsWith("bitcoin:")) {
		return "";
	}

	const withoutScheme = trimmed.slice("bitcoin:".length);
	const addressPart = withoutScheme.split("?")[0]?.trim() || "";
	return isBtcAddress(addressPart) ? addressPart : "";
}

function normalizeOdinBabyInternalPath(parsedUrl: URL) {
	const pathname = parsedUrl.pathname || "/";
	if (!pathname.startsWith("/") || pathname.startsWith("//")) {
		return null;
	}

	return `${pathname}${parsedUrl.search}${parsedUrl.hash}`;
}

function normalizeOdinFunInternalPath(parsedUrl: URL) {
	const pathname = parsedUrl.pathname.replace(/\/+$/, "") || "/";
	const segments = pathname.split("/").filter(Boolean);

	if (segments.length >= 2 && segments[0] === "token") {
		return `/plaza/token/${segments[1]}${parsedUrl.search}${parsedUrl.hash}`;
	}

	if (segments.length >= 2 && segments[0] === "user") {
		return `/address/${segments[1]}${parsedUrl.search}${parsedUrl.hash}`;
	}

	return null;
}

function getSupportedScanInternalPath(parsedUrl: URL) {
	const hostname = parsedUrl.hostname.toLowerCase();

	if (hostname === "odin.baby" || hostname === "www.odin.baby") {
		return normalizeOdinBabyInternalPath(parsedUrl);
	}

	if (hostname === "odin.fun") {
		return normalizeOdinFunInternalPath(parsedUrl);
	}

	return null;
}

async function fetchAddressSearch(query: string) {
	const response = await fetch(
		`https://api.odin.fun/v1/search?q=${encodeURIComponent(query)}`,
	);

	if (!response.ok) {
		throw new Error(`Address search failed: ${response.status}`);
	}

	const data = (await response.json()) as SearchResponse;
	return data.data || [];
}

export async function checkOdinUserExists(principal: string) {
	const response = await fetch(
		`https://api.odin.fun/v1/user/${encodeURIComponent(principal)}`,
	);

	return response.ok;
}

export async function resolveRecipientInput(
	input: string,
): Promise<RecipientResolution> {
	const trimmed = input.trim();

	if (!trimmed) {
		return { kind: "empty", input: trimmed };
	}

	if (isValidPrincipal(trimmed)) {
		try {
			const exists = await checkOdinUserExists(trimmed);
			return exists
				? {
						kind: "principal_odin_user",
						input: trimmed,
						principal: trimmed,
					}
				: {
						kind: "principal_non_odin_user",
						input: trimmed,
						principal: trimmed,
					};
		} catch (error) {
			return {
				kind: "lookup_error",
				input: trimmed,
				error: error instanceof Error ? error.message : "Lookup failed",
			};
		}
	}

	const btcAddress = extractBtcAddressFromQr(trimmed);
	if (!btcAddress) {
		return { kind: "invalid", input: trimmed };
	}

	try {
		const addressResults = await fetchAddressSearch(btcAddress);
		const matchedUser =
			addressResults.find(
				(item) => item.type === "user" && hasExactAddressMatch(item.entity, btcAddress),
			) || null;

		if (!matchedUser?.id) {
			return {
				kind: "btc_external",
				input: trimmed,
				address: btcAddress,
			};
		}

		return {
			kind: "btc_bound",
			input: trimmed,
			address: btcAddress,
			principal: matchedUser.id,
		};
	} catch (error) {
		return {
			kind: "lookup_error",
			input: trimmed,
			error: error instanceof Error ? error.message : "Lookup failed",
		};
	}
}

export async function resolveScannedContent(
	input: string,
): Promise<ScanResolution> {
	const raw = input.trim();

	if (!raw) {
		return {
			kind: "unrecognized",
			raw,
			displayText: "",
		};
	}

	try {
		const parsedUrl = new URL(raw);
		const hostname = parsedUrl.hostname.toLowerCase();
		const internalPath = getSupportedScanInternalPath(parsedUrl);
		if (internalPath) {
			return {
				kind: "supported_url",
				raw,
				url: parsedUrl.toString(),
				host: hostname,
				internalPath,
			};
		}
	} catch {}

	const recipientResolution = await resolveRecipientInput(raw);

	switch (recipientResolution.kind) {
		case "principal_odin_user":
			return {
				kind: "principal_odin_user",
				raw,
				principal: recipientResolution.principal,
			};
		case "btc_bound":
			return {
				kind: "btc_bound",
				raw,
				address: recipientResolution.address,
				principal: recipientResolution.principal,
			};
		case "btc_external":
			return {
				kind: "btc_external",
				raw,
				address: recipientResolution.address,
			};
		case "lookup_error":
			return {
				kind: "lookup_error",
				raw,
				displayText: raw,
				error: recipientResolution.error,
			};
		default:
			return {
				kind: "unrecognized",
				raw,
				displayText: raw,
			};
	}
}
